import { z } from 'zod';

/**
 * The currencies the platform supports quoting in — ISO 4217.
 *
 * ## Why it lives in contracts rather than in the api's settings registry
 *
 * It started there, beside `billing.currency`'s definition, because the admin's
 * `MoneyInput` was its only reader. P22c2 gave it a second one: the member app
 * reads the resolved currency from `GET /api/app-settings` and has to **parse**
 * it. A value a client parses is a contract (U3), and the alternative — the
 * mobile app re-declaring the same nine codes — is the second source of truth
 * that drifts the first time a tenth is added.
 *
 * ## Why the list is closed
 *
 * `billing.currency`'s only editor is a `select`, which cannot express a value
 * outside its options, so a permissive schema behind it would be a promise the
 * one editor refuses (P22b3). The list is the platform's *supported* set and the
 * two agree; widening it is one line here plus a translated name in the admin
 * catalogue.
 */
export const BILLING_CURRENCIES = [
  'SAR',
  'AED',
  'KWD',
  'QAR',
  'BHD',
  'OMR',
  'EGP',
  'JOD',
  'USD',
] as const;

export const BillingCurrencySchema = z.enum(BILLING_CURRENCIES);

export type BillingCurrency = (typeof BILLING_CURRENCIES)[number];

/**
 * A three-letter ISO code as a **store** reports it, deliberately wider than
 * `BillingCurrencySchema`: Apple settles in the buyer's storefront currency,
 * which this deployment does not choose and may not sell in directly. The fee
 * ledger and the payments ledger both record what the door said (D9a, P31).
 */
export const ReportedCurrencySchema = z.string().trim().length(3).toUpperCase();
